import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { CheckCircleOutlined, DeleteOutlined, RollbackOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  COMPETENCY_PERMS as P,
  COMPETENCY_VIEW_ANY,
  useCompetencyAction,
  useCompetencyAssessment,
  useCompetencyCan,
  useCompetencyOverview,
  useMyTeam,
  useTrainingCourses,
} from '@/hooks/useCompetency';
import { competencyService } from '@/services/competency.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_COMPETENCY_ASSESSMENT } from '@/constants/document-entity-types';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import {
  ASSESSMENT_PARAMETERS,
  ASSESSMENT_TYPES,
  COMPETENCY_BASE,
  GRADE_COLOR,
  GRADE_LABEL,
  MAX_PARAMETER_SCORE,
  PROBATION_TYPES,
  STATUS_COLOR,
  employeeLabel,
  gradeForTotal,
  recommendationsFor,
} from './competencyDisplay';

const { Text } = Typography;
const SCORE_OPTIONS = Array.from({ length: MAX_PARAMETER_SCORE + 1 }, (_, i) => ({ value: i, label: String(i) }));

const AssessmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const canViewAll = useCompetencyCan(...COMPETENCY_VIEW_ANY);
  const canClose = useCompetencyCan(P.assessmentClose);
  const { data: row, isLoading, error } = useCompetencyAssessment(isNew ? undefined : id);
  const { data: allEmployees = [] } = useCompetencyOverview(undefined, canViewAll);
  const { data: team = [] } = useMyTeam(!canViewAll);
  const employees = canViewAll ? allEmployees : team;
  const { data: courses = [] } = useTrainingCourses({ active: '1' }, canClose);
  const [returning, setReturning] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [closing, setClosing] = useState(false);
  const [closeForm] = Form.useForm();

  const watched = Form.useWatch([], form) as Record<string, any> | undefined;
  const type: string = watched?.assessment_type ?? row?.assessment_type ?? 'Yearly';
  const scores = ASSESSMENT_PARAMETERS.map((p) => watched?.[p.key]);
  const scored = scores.filter((s) => s !== null && s !== undefined).length;
  const total = scores.reduce((sum: number, s) => sum + (Number(s) || 0), 0);
  const grade = scored === ASSESSMENT_PARAMETERS.length ? gradeForTotal(total) : null;
  const recommendations = recommendationsFor(type);
  const belowC = !!grade && ['D', 'E'].includes(grade);

  useEffect(() => {
    if (row) {
      form.setFieldsValue({ ...row, assessment_date: row.assessment_date ? dayjs(row.assessment_date) : null });
    } else if (isNew) {
      form.setFieldsValue({ employee_id: params.get('employee_id') ?? undefined, assessment_type: 'Yearly', assessment_date: dayjs() });
    }
  }, [row, isNew, params, form]);

  useEffect(() => {
    if (watched?.recommendation && !recommendations.includes(watched.recommendation)) form.setFieldsValue({ recommendation: undefined });
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCompetencyAction(async (v: Record<string, any>) => {
    const payload = { ...v, assessment_date: v.assessment_date ? dayjs(v.assessment_date).format('YYYY-MM-DD') : null };
    return competencyService.saveAssessment(isNew ? null : (id as string), payload);
  }, 'Assessment saved');
  const submit = useCompetencyAction(() => competencyService.submitAssessment(id as string), 'Submitted to HR');
  const sendBack = useCompetencyAction(() => competencyService.returnAssessment(id as string, returnReason), 'Returned to the evaluator');
  const close = useCompetencyAction(
    (v: { hr_notes?: string; confirmation_date?: dayjs.Dayjs; training_course_ids?: string[] }) =>
      competencyService.closeAssessment(id as string, {
        hr_notes: v.hr_notes || null,
        confirmation_date: v.confirmation_date ? v.confirmation_date.format('YYYY-MM-DD') : null,
        training_course_ids: v.training_course_ids ?? [],
      }),
    'Assessment closed'
  );
  const remove = useCompetencyAction(() => competencyService.deleteAssessment(id as string), 'Assessment deleted');

  const employee = useMemo(
    () => employees.find((e) => e.id === (watched?.employee_id ?? row?.employee_id)),
    [employees, watched?.employee_id, row?.employee_id]
  );

  if (!isNew && isLoading) return <Spin style={{ margin: 48 }} />;
  if (!isNew && !row) {
    return <Alert type="error" showIcon style={{ margin: 24 }} message={(error as Error)?.message || 'Assessment not found or not accessible'} />;
  }

  const editable = isNew || row?.status === 'Draft';
  const onSave = async () => {
    const v = await form.validateFields();
    const saved = await save.mutateAsync(v);
    if (isNew && saved?.id) navigate(`${COMPETENCY_BASE}/assessments/${saved.id}`, { replace: true });
    return saved;
  };

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title={isNew ? 'New competency assessment' : `${row?.assessment_number} - ${row?.employee_name}`}
        extra={
          <Space wrap>
            {row && <Tag color={STATUS_COLOR[row.status]}>{row.status}</Tag>}
            {row?.hr_outcome && <Tag color="purple">{row.hr_outcome}</Tag>}
            {row && (
              <ReportDocumentActions
                reportKey="competency-assessment"
                params={{ id: row.id }}
                documentId={row.id}
                sendPermissions={[P.assessmentClose]}
              />
            )}
            {editable && (
              <Button icon={<SaveOutlined />} loading={save.isPending} onClick={onSave}>
                Save
              </Button>
            )}
            {row?.status === 'Draft' && (
              <Popconfirm
                title="Submit to HR?"
                description="The assessment is saved first. It cannot be edited after submission unless HR returns it."
                onConfirm={async () => {
                  await save.mutateAsync(await form.validateFields());
                  await submit.mutateAsync();
                }}
              >
                <Button type="primary" icon={<SendOutlined />} loading={submit.isPending}>
                  Submit to HR
                </Button>
              </Popconfirm>
            )}
            {row?.status === 'Draft' && (
              <Popconfirm
                title="Delete this draft?"
                onConfirm={async () => {
                  await remove.mutateAsync();
                  navigate(`${COMPETENCY_BASE}/assessments`);
                }}
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
            {row?.status === 'Submitted' && canClose && (
              <>
                <Button icon={<RollbackOutlined />} onClick={() => setReturning(true)}>
                  Return
                </Button>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setClosing(true)}>
                  HR close
                </Button>
              </>
            )}
          </Space>
        }
      />

      {row?.status === 'Draft' && row.hr_notes?.startsWith('Returned:') && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={row.hr_notes} />
      )}

      <Form form={form} layout="vertical" disabled={!editable}>
        <Card title="Employee" style={{ marginBottom: 16 }}>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  disabled={!isNew}
                  placeholder={!canViewAll && !team.length ? 'No team members found' : undefined}
                  options={employees.map((e) => ({ value: e.id, label: employeeLabel(e) }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={5}>
              <Form.Item name="assessment_type" label="Type of assessment" rules={[{ required: true }]}>
                <Select disabled={!isNew} options={ASSESSMENT_TYPES.map((t) => ({ value: t, label: t }))} />
              </Form.Item>
            </Col>
            {type === 'Other' && (
              <Col xs={24} md={5}>
                <Form.Item name="assessment_type_other" label="Describe" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
            )}
            <Col xs={12} md={3}>
              <Form.Item name="assessment_date" label="Date of assessment" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={type === 'Other' ? 3 : 8}>
              <Form.Item name="evaluator_employee_id" label="Evaluator" tooltip="Defaults to you when submitted">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={(canViewAll ? allEmployees : []).map((e) => ({ value: e.id, label: employeeLabel(e) }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Descriptions size="small" column={{ xs: 1, md: 4 }}>
            <Descriptions.Item label="Designation">{row?.job_title ?? employee?.job_title ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Department">{row?.department_name ?? employee?.department_name ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Date of joining">{formatDate(row?.join_date ?? employee?.join_date)}</Descriptions.Item>
            <Descriptions.Item label="Confirmation">{row?.confirmation_status ?? employee?.confirmation_status ?? '-'}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          title="Performance indicators"
          style={{ marginBottom: 16 }}
          extra={
            <Space>
              <Text>
                Total {total} / {ASSESSMENT_PARAMETERS.length * MAX_PARAMETER_SCORE}
              </Text>
              {grade ? <Tag color={GRADE_COLOR[grade]}>{`Grade ${grade} - ${GRADE_LABEL[grade]}`}</Tag> : <Tag>{`${scored} of 8 scored`}</Tag>}
            </Space>
          }
        >
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={ASSESSMENT_PARAMETERS}
            columns={[
              { title: '#', width: 40, render: (_, __, i) => i + 1 },
              { title: 'Parameter', dataIndex: 'label' },
              {
                title: `Score (0 - ${MAX_PARAMETER_SCORE})`,
                width: 300,
                render: (_, p) => (
                  <Form.Item name={p.key} style={{ margin: 0 }}>
                    <Radio.Group options={SCORE_OPTIONS} optionType="button" buttonStyle="solid" size="small" />
                  </Form.Item>
                ),
              },
            ]}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            A 35-40 Exceptional | B 30-34 Exceed expectation | C 25-29 Meet expectation | D 20-24 Below average | E below 20 Poor performance.
            The minimum requirement is grade C.
          </Text>
        </Card>

        <Card title="Evaluator recommendation" style={{ marginBottom: 16 }}>
          {belowC && PROBATION_TYPES.includes(type) && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Below grade C the employee cannot be confirmed. Extend probation, give additional training or terminate."
            />
          )}
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="comments" label="Evaluator's comments">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="training_requirements"
                label="Training requirements"
                rules={[{ required: watched?.recommendation === 'Additional training', message: 'Describe the training required' }]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="recommendation" label="Recommendation">
                <Radio.Group
                  options={recommendations.map((r) => ({ value: r, label: r, disabled: r === 'Confirm' && belowC }))}
                  optionType="button"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>

      {row?.status === 'Closed' && (
        <Card title="HR review" style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={{ xs: 1, md: 3 }}>
            <Descriptions.Item label="Outcome">{row.hr_outcome}</Descriptions.Item>
            <Descriptions.Item label="Closed">{formatDate(row.hr_closed_at)}</Descriptions.Item>
            <Descriptions.Item label="Notes">{row.hr_notes ?? '-'}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {row && <EntityAttachmentsCard entityType={DOCUMENT_ENTITY_TYPE_COMPETENCY_ASSESSMENT} entityId={row.id} title="Signed form and evidence" />}

      <Modal
        title="Return to evaluator"
        open={returning}
        onCancel={() => setReturning(false)}
        okText="Return"
        okButtonProps={{ disabled: !returnReason.trim() }}
        confirmLoading={sendBack.isPending}
        onOk={async () => {
          await sendBack.mutateAsync();
          setReturning(false);
          setReturnReason('');
        }}
      >
        <Input.TextArea rows={3} placeholder="Reason" value={returnReason} onChange={(e) => setReturnReason(e.target.value)} />
      </Modal>

      <Modal
        title="HR close"
        open={closing}
        onCancel={() => setClosing(false)}
        okText="Close assessment"
        confirmLoading={close.isPending}
        onOk={async () => {
          await close.mutateAsync(closeForm.getFieldsValue());
          setClosing(false);
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Recommendation: <Text strong>{row?.recommendation}</Text> (grade {row?.grade}, total {row?.total_score})
          </Text>
          {row?.recommendation === 'Confirm' && <Alert type="info" showIcon message="The employee is confirmed in HR." />}
          {row?.recommendation === 'Extend probation' && <Alert type="info" showIcon message="Probation is extended by 6 months in HR and a re-assessment follows." />}
          {row?.recommendation === 'Terminate' && (
            <Alert type="warning" showIcon message="Recorded as termination initiated. Process the exit in HR separately." />
          )}
          <Form form={closeForm} layout="vertical">
            {row?.recommendation === 'Confirm' && (
              <Form.Item name="confirmation_date" label="Confirmation date" initialValue={dayjs()}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            )}
            <Form.Item name="training_course_ids" label="Add training to the annual schedule">
              <Select
                mode="multiple"
                allowClear
                optionFilterProp="label"
                options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
              />
            </Form.Item>
            <Form.Item name="hr_notes" label="HR notes">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </div>
  );
};

export default AssessmentDetailPage;
